// @givmo/app-charity — the ONE frame a served Charity Terms document is read in.
//
// Two surfaces read this instrument: the served read/accept page (`terms-page.tsx`, an Owner
// re-reading or re-accepting) and the claim wizard's bundled acceptance step (CPA-WEB12, a claimant
// with no membership yet). Both show the SAME two things in the same order — the effective date and
// the body from `content_markdown` — so both get them from here.
//
// NO VERSION NUMBER IN THE CHROME (D-CP-80 for the page, 2026-09-18; D-CP-84 for the wizard,
// 2026-10-18, CPA-WEB78). Jonathan: the version number is record-keeping, not something a charity
// admin needs to see. It keeps riding the acceptance echo with the content hash, so every acceptance
// stays pinned to the exact document, and it stays in the signed body's own text; the frame names
// neither.
//
// WHY SHARED RATHER THAN COPIED. The effective date is the reader's visible anchor on the legal
// instrument a person is about to bind their organization to. Two copies of that header is two places
// for it to drift out of step with the other surface, on the one screen pair where a drift would mean
// two claimants reading differently-framed documents. There is exactly one place in this app that
// renders a legal body.
//
// LEGAL RAILS this frame carries for both callers:
//   - the body is only `CharityTermsOut.content_markdown`, passed unchanged to the conservative
//     structural presenter (`TermsBody`). No legal substance is authored here.
//   - `content_hash` is NEVER rendered. It pins the §318 acceptance row to exactly the bytes shown
//     and must keep riding the acceptance/submit body, but a sha256 tells a small nonprofit nothing.
//     Do not "restore" a hash chip, a receipt hash line, or a copy-hash affordance.
//   - the effective date is pinned to UTC ({@link formatTermsEffectiveDate}) because it belongs to the
//     legal instrument and must not shift under the reader's timezone.

import { Card, Icon, testIdProp } from '@givmo/design-system';
import type { CharityTermsOut } from '@givmo/api-client';
import { FileText } from 'lucide-react';
import { TermsBody } from './terms-body';

/**
 * The document as a WINDOW, not a page. The card is the frame: its effective-date bar is pinned at
 * the top and only the body below scrolls, so whatever the caller renders AFTER this card — an
 * affirmation, a signer block, an Accept button — stays on screen without the reader scrolling the
 * page to reach it.
 *
 * `className` is how the two callers differ, and they differ genuinely: the terms page gives the
 * window the single flexible slot in a viewport-fitted column (`min-h-0 flex-1`, the pair that lets a
 * flex child shrink below its content and scroll), while the wizard sits in a normally-flowing page
 * and gives it a bounded height. Everything else is identical.
 */
export function TermsDocumentWindow({
  terms,
  testIdPrefix,
  className,
  heading,
  hint,
}: {
  terms: CharityTermsOut;
  /** Prefix for this surface's testids — `<prefix>-document`, `-effective`, `-document-scroll`, `-body`. */
  testIdPrefix: string;
  className?: string;
  /** Optional line above the date bar, for a surface where the card is not under its own page title. */
  heading?: string;
  /**
   * Optional line under the date bar.
   *
   * It exists for the WIZARD and not for the terms page, and the difference is real rather than
   * cosmetic: the terms page gives this window the whole viewport, so the cut at the bottom of the
   * screen reads as "there is more below" the way any long document does. In the wizard the window is a
   * bounded box in the middle of a page that itself scrolls, and a reader scrolling PAST it can reach
   * the affirmations without ever discovering that the box has its own scroll — which would mean
   * accepting an agreement after reading one screenful of it.
   */
  hint?: string;
}) {
  const effective = formatTermsEffectiveDate(terms.effective_at);
  return (
    <Card className={className} testId={`${testIdPrefix}-document`}>
      <div className="shrink-0 border-b border-border-subtle px-20 py-16">
        {heading !== undefined ? (
          <p className="mb-8 flex items-center gap-8 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
            <Icon icon={FileText} size={16} className="shrink-0 text-brand-700" aria-hidden />
            {heading}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-8">
          <span
            className="text-s13 text-text-tertiary"
            {...testIdProp(`${testIdPrefix}-effective`)}
          >
            Effective {effective}
          </span>
        </div>
        {hint !== undefined ? (
          <p
            className="mt-8 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp(`${testIdPrefix}-hint`)}
          >
            {hint}
          </p>
        ) : null}
      </div>
      {/* Focusable and labeled: a scroll region a keyboard cannot reach locks keyboard-only readers
          out of the legal text entirely (WCAG 2.1.1). `overscroll-contain` keeps a flick at the end
          of the terms from scrolling the page behind them. */}
      <div
        role="region"
        aria-label="Charity Terms document"
        tabIndex={0}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-24 py-28 outline-none focus-visible:shadow-ring"
        {...testIdProp(`${testIdPrefix}-document-scroll`)}
      >
        <TermsBody text={terms.content_markdown} testId={`${testIdPrefix}-body`} />
      </div>
    </Card>
  );
}

/**
 * The instrument's effective date, pinned to UTC.
 *
 * UTC deliberately: this date belongs to the legal instrument, so it must not shift under a reader in
 * another timezone — contrast a user-event date (an acceptance timestamp), which is rendered
 * viewer-local. An absent value reads "not provided" rather than being omitted, because a missing
 * anchor on a legal document is worth saying out loud; an unparseable one prints verbatim rather than
 * inventing a date.
 */
export function formatTermsEffectiveDate(value: string | null | undefined): string {
  if (!value) return 'not provided';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}
